import { Box, Stack, Typography } from '@mui/material';

import { useNavigateToRoute } from '@/components/navigation/useNavigateToRoute';
import { useMultiDocumentUploaderContext } from '@/modules/documents/MultiDocumentUploader/context/multiDocumentUploader.context';
import { MultiDocumentUploaderProvider } from '@/modules/documents/MultiDocumentUploader/context/MultiDocumentUploader.provider';
import { useFeatureFlag } from '@/modules/featureFlags/useFeatureFlag';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { ROUTE_KEYS } from '@/navigation/constants';
import { COLORS } from '@/styles/tokens/colors';
import { WorkflowTrackerKind } from '@/types/schema';

import { ClientOnboardingDeliverableModalBodyProps } from '../../types';
import { AssetIntegrationPromptCard } from '../components/AssetIntegrationPromptCard/AssetIntegrationPromptCard';
import { useHouseholdConnectionDetails } from '../components/AssetIntegrationPromptCard/useHouseholdConnectionDetails';
import { GenericDocumentProcessingModalBody } from './GenericDocumentProcessingModalBody';

function BuildOutHouseholdModalBodyInner({
  onChildPhaseChange,
  onClose,
}: ClientOnboardingDeliverableModalBodyProps) {
  const { householdId } = useHouseholdDetailsContext();
  const { uploadedFiles, selectedExistingDocuments } =
    useMultiDocumentUploaderContext();
  const { integrationAvailable, showIntegrationPromptCard } =
    useHouseholdConnectionDetails();
  // When people suggestions are auto-accepted there is nothing for the user to
  // review under People, so direct them to trusts instead. Auto-accept is only
  // active on the KG v2 data path.
  const useKGV2 = useFeatureFlag('aikg_v2_associations_poc');

  if (!householdId) return null;

  const relevantFilesCount = [...uploadedFiles, ...selectedExistingDocuments]
    .length;

  const showIntegrationOption =
    integrationAvailable && relevantFilesCount === 0;

  const additionalUploadContent = showIntegrationOption ? (
    <>
      {showIntegrationPromptCard && (
        <>
          <AssetIntegrationPromptCard />
          <Box textAlign="center" my={2}>
            <Typography variant="body-xs">or</Typography>
          </Box>
        </>
      )}

      <Stack direction="column" gap={0.5}>
        <Typography variant="title-l" color={COLORS.NAVY['700']}>
          Create with document(s)
        </Typography>
        <Typography variant="body-s" color={COLORS.GRAY['500']}>
          Luminary AI will extract and suggest client profile data
        </Typography>
      </Stack>
    </>
  ) : null;

  const config = {
    allowSelectExistingDocuments: false,
    subheader:
      'Luminary AI will process each document and identify individuals, organizations, entities, and sub-trusts for you to review.',
    workflowTrackerKind: WorkflowTrackerKind.DocumentExtraction,
    useAIWorkflow: true,
    processingHeader: (documentNoun: string) => `Processing ${documentNoun}...`,
    processingSubheader: (
      <>
        We are processing your documents and will notify you when household
        suggestions are ready for review.
        <strong> This process may take up to several minutes.</strong>
      </>
    ),
    successHeader: 'Success! Household suggestions ready',
    successSubheader: 'Your household suggestions are ready for review',
    errorMessage:
      'We failed to extract household suggestions from these documents. Please upload new documents or re-select the previous document to try again.',
    completionButtonLabel: useKGV2
      ? 'Start with trust suggestions'
      : 'Start with people suggestions',
    onCompletionClick: (
      navigate: ReturnType<typeof useNavigateToRoute>['navigate'],
      householdId: string
    ) => {
      onClose();
      navigate(
        useKGV2
          ? ROUTE_KEYS.HOUSEHOLD_ESTATE_TRUSTS
          : ROUTE_KEYS.HOUSEHOLD_DETAILS_PEOPLE_ORGANIZATIONS,
        {
          householdId,
        }
      );
    },
    additionalUploadContentAbove: additionalUploadContent,
  };

  return (
    <GenericDocumentProcessingModalBody
      onClose={onClose}
      onChildPhaseChange={onChildPhaseChange}
      config={config}
    />
  );
}

export function BuildOutHouseholdModalBody(
  props: ClientOnboardingDeliverableModalBodyProps
) {
  return (
    <MultiDocumentUploaderProvider>
      <BuildOutHouseholdModalBodyInner {...props} />
    </MultiDocumentUploaderProvider>
  );
}
